import { Lightbulb } from "lucide-react";
import type { Block } from "@/lib/site";
import { imageSrc } from "@/lib/site";
import { Heart } from "@/components/Doodles";
import { AFF_REL, goUrl, useCraftName } from "@/lib/affiliate";

const H2 = ({ children }: { children: string }) => (
  <h2 className="mt-10 flex items-center gap-2 font-heading text-2xl font-semibold text-[#23324A]"><Heart className="lh-only ink-doodle h-6 w-6 shrink-0" />{children}</h2>
);

function Story({ b }: { b: Extract<Block, { type: "story" }> }) {
  return (
    <aside className="post-story mt-8 -rotate-[0.4deg] rounded-[22px] border-2 border-dashed border-[#23324A] bg-[#FFFBEA] p-6" data-testid="post-story">
      <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-[#78716C]">{b.label}</p>
      {b.text.map((t, i) => <p key={i} className="mt-2 text-lg leading-relaxed text-[#23324A]">{t}</p>)}
    </aside>
  );
}

function Timeline({ b }: { b: Extract<Block, { type: "timeline" }> }) {
  return (
    <section>
      <H2>{b.title}</H2>
      <ol className="mt-4 space-y-3 border-l-2 border-[#E7E5E4] pl-5">
        {b.items.map((it, i) => (
          <li key={i} className="relative">
            <span className="absolute -left-[34px] top-0.5 rounded-full border border-[#23324A]/25 bg-white px-1.5 text-[10px] font-bold text-[#23324A]">{it.time}</span>
            <p className="leading-relaxed text-[#23324A]/90">{it.text}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Steps({ b }: { b: Extract<Block, { type: "steps" }> }) {
  return (
    <ol className="mt-8 space-y-4">
      {b.items.map((s, i) => (
        <li key={i} className="calm-card flex gap-4 p-5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F5F5F4] font-heading text-lg font-semibold text-[#23324A]">{i + 1}</span>
          <div><p className="font-heading text-lg font-semibold text-[#23324A]">{s.title}</p><p className="mt-1 leading-relaxed text-[#23324A]/85">{s.text}</p></div>
        </li>
      ))}
    </ol>
  );
}

function Compare({ b }: { b: Extract<Block, { type: "compare" }> }) {
  const col = (label: string, items: string[], bg: string) => (
    <div className="rounded-2xl p-4" style={{ background: bg }}>
      <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-[#23324A]/70">{label}</p>
      <ul className="space-y-1 text-sm text-[#23324A]">{items.map((t, i) => <li key={i}>• {t}</li>)}</ul>
    </div>
  );
  return (
    <section className="mt-8">
      <p className="mb-3 font-heading text-lg font-semibold text-[#23324A]">{b.title}</p>
      <div className="grid gap-3 sm:grid-cols-2">{col("Before", b.before, "#FFFFFF")}{col("After", b.after, "#F5F5F4")}</div>
    </section>
  );
}

function Table({ b }: { b: Extract<Block, { type: "table" }> }) {
  return (
    <section>
      <H2>{b.title}</H2>
      <table className="mt-4 w-full overflow-hidden rounded-2xl border border-[#23324A]/15 bg-white text-sm">
        <tbody>{b.rows.map((r, i) => (
          <tr key={i} className={i % 2 ? "bg-[#FDFBF7]" : ""}>
            <td className="px-4 py-2.5 text-[#23324A]">{r[0]}</td>
            <td className="px-4 py-2.5 text-right font-semibold text-[#1C1917]">{r[1]}</td>
          </tr>
        ))}</tbody>
      </table>
    </section>
  );
}

function Material({ m, src }: { m: string; src?: string }) {
  const craft = useCraftName();
  if (!src) return <li>□ {m}</li>;
  const link = (store: "amazon" | "craft", label: string) => (
    <a href={goUrl(store, m, src)} target="_blank" rel={AFF_REL} className="text-xs font-semibold text-[#57534E] underline underline-offset-2 hover:text-[#1C1917]" data-testid={`material-${store}-link`}>{label}</a>
  );
  return (
    <li>□ {m}<span className="block whitespace-nowrap pl-4">{link("amazon", "Amazon")} · {link("craft", craft)}</span></li>
  );
}

function Project({ b, src }: { b: Extract<Block, { type: "project" }>; src?: string }) {
  return (
    <section className="calm-card mt-10 rounded-[24px] p-6" data-testid="post-project">
      <h2 className="font-heading text-2xl font-semibold text-[#23324A]">{b.title}</h2>
      {b.image && (
        <figure className="relative mt-4 overflow-hidden rounded-[18px]" data-testid="project-image">
          <img src={imageSrc(b.image)} alt={`Finished ${b.title}`} className="aspect-[4/3] w-full object-cover" loading="lazy" />
          {b.image_ai && <figcaption className="absolute bottom-2 left-2 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-[#23324A]" data-testid="example-image-badge">Example image</figcaption>}
        </figure>
      )}
      <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold text-[#23324A]">
        {[b.age, b.time, b.cost].filter(Boolean).map((m) => <span key={m} className="pill">{m}</span>)}
      </div>
      <div className="mt-5 grid gap-6 sm:grid-cols-[1fr_1.6fr]">
        <div>
          <p className="label">You'll need</p>
          <ul className="space-y-1.5 text-sm text-[#23324A]">{b.materials.map((m, i) => <Material key={i} m={m} src={src} />)}</ul>
        </div>
        <div>
          <p className="label">Steps</p>
          <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed text-[#23324A]">{b.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
        </div>
      </div>
      {b.tip && <p className="mt-5 flex gap-2 rounded-xl bg-[#F5F5F4] p-3 text-sm text-[#23324A]"><Lightbulb className="h-4 w-4 shrink-0" strokeWidth={2.5} />{b.tip}</p>}
    </section>
  );
}

export function PostBlocks({ blocks, src }: { blocks: Block[]; src?: string }) {
  return (
    <>
      {blocks.map((b, i) => {
        switch (b.type) {
          case "p": return <p key={i} className="mt-6 text-lg leading-relaxed text-[#23324A]">{b.text}</p>;
          case "h2": return <H2 key={i}>{b.text}</H2>;
          case "list": return <ul key={i} className="mt-4 list-disc space-y-2 pl-6 text-[#23324A]/90">{b.items.map((t, j) => <li key={j}>{t}</li>)}</ul>;
          case "tip": return <p key={i} className="mt-8 flex gap-3 rounded-2xl bg-[#F5F5F4] p-4 text-[#23324A]"><Lightbulb className="h-5 w-5 shrink-0 text-[#78716C]" strokeWidth={2.5} />{b.text}</p>;
          case "story": return <Story key={i} b={b} />;
          case "timeline": return <Timeline key={i} b={b} />;
          case "steps": return <Steps key={i} b={b} />;
          case "compare": return <Compare key={i} b={b} />;
          case "table": return <Table key={i} b={b} />;
          case "project": return <Project key={i} b={b} src={src} />;
          default: return null;
        }
      })}
    </>
  );
}
